import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, ButtonBase, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BILL_CONCERN_TOPICS, BillConcernTopic, getBillConcernOptions } from '../utils/billConfig';

interface BillConcernDialogProps {
  open: boolean;
  onClose: () => void;
}

const BillConcernDialog: React.FC<BillConcernDialogProps> = ({ open, onClose }) => {
  const [topic, setTopic] = useState<BillConcernTopic | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const selectedTopic = BILL_CONCERN_TOPICS.find((item) => item.id === topic);
  const options = getBillConcernOptions().filter((item) => item.topic === topic);

  useEffect(() => {
    if (open) {
      headingRef.current?.focus();
    }
  }, [topic, open]);

  const rowStyles: SxProps<Theme> = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    p: { xs: 2, sm: 2.5 },
    textAlign: 'left',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    color: 'text.primary',
    '&:hover, &.Mui-focusVisible': {
      borderColor: 'primary.main',
      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
    },
    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="bill-concern-title"
      aria-describedby="bill-concern-description"
      TransitionProps={{
        onEntered: () => headingRef.current?.focus(),
        onExited: () => setTopic(null),
      }}
      PaperProps={{ sx: { m: 2, width: 'calc(100% - 32px)', borderRadius: 3, maxHeight: 'calc(100% - 32px)' } }}
    >
      <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 1.5 }}>
          {selectedTopic ? selectedTopic.title : 'Find your starting point'}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close bill finder" edge="end">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogTitle
        id="bill-concern-title"
        ref={headingRef}
        tabIndex={-1}
        sx={{ px: { xs: 3, sm: 4 }, pt: 1, pb: 1, fontSize: { xs: '1.6rem', sm: '2rem' }, fontWeight: 800 }}
      >
        {topic ? 'Which sounds familiar?' : 'What concerns you most?'}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 3, sm: 4 }, pb: 3 }}>
        <Typography id="bill-concern-description" color="text.secondary" sx={{ lineHeight: 1.6, mb: 3 }}>
          {topic ? 'Choose a concern to read the bill that addresses it.' : 'Choose a topic. Find a proposed reform.'}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.25 }}>
          {topic
            ? options.map((option) => (
                <Box component="li" key={option.route}>
                  <ButtonBase component={Link} to={`/bill/${option.route}`} onClick={onClose} sx={rowStyles}>
                    <Box>
                      <Typography component="span" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.5 }}>
                        {option.problem}
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {option.pageTitle}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 20, flexShrink: 0, color: 'primary.main' }} />
                  </ButtonBase>
                </Box>
              ))
            : BILL_CONCERN_TOPICS.map((item) => (
                <Box component="li" key={item.id}>
                  <ButtonBase onClick={() => setTopic(item.id)} sx={rowStyles}>
                    <Box>
                      <Typography component="span" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.5 }}>
                        {item.title}
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon aria-hidden="true" sx={{ fontSize: 20, flexShrink: 0, color: 'primary.main' }} />
                  </ButtonBase>
                </Box>
              ))}
        </Box>
        <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          {topic ? (
            <Button onClick={() => setTopic(null)} startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>
              All topics
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No personal details needed.
            </Typography>
          )}
          <Button component={Link} to="/family-court-reform-bills" onClick={onClose} sx={{ textTransform: 'none' }}>
            Browse all bills
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BillConcernDialog;
